import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Search, Phone } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { NAV, CONTACT, waLink, WA } from "../../data/content";
import { S } from "../../data/strings";

const WhatsappIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.174.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const LangToggle = ({ dark }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="language-switcher"
      className={"inline-flex items-center rounded-full border p-0.5 text-xs font-cinzel tracking-wide " + (dark ? "border-goldlight/40" : "border-gold/40")}
    >
      {["en", "hi"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={
            "px-2.5 py-1 rounded-full transition-colors duration-300 " +
            (lang === l
              ? "bg-gold text-burgundy"
              : dark ? "text-goldlight/80 hover:text-goldlight" : "text-maroon/70 hover:text-maroon")
          }
        >
          {l === "en" ? "EN" : "हिं"}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="royal-header"
        className={
          "fixed top-0 inset-x-0 z-50 transition-all duration-500 " +
          (scrolled
            ? "bg-ivory/90 backdrop-blur-xl border-b border-gold/25 shadow-[0_4px_24px_rgba(61,12,26,0.05)]"
            : "bg-transparent")
        }
      >
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <a href="#home" data-testid="header-logo" className="flex flex-col leading-none group">
              <span className="font-serif text-xl sm:text-2xl font-medium tracking-tight text-maroon">
                {t(S.brand)}
              </span>
              <span className="font-cinzel text-[9px] sm:text-[10px] tracking-luxe uppercase text-gold mt-0.5">
                {t(S.brandSub)}
              </span>
            </a>

            {/* Desktop nav */}
            <nav className="hidden xl:flex items-center gap-6">
              {NAV.map((n) => (
                <a
                  key={n.id + n.label.en}
                  href={`#${n.id}`}
                  data-testid={`nav-${n.id}`}
                  className="relative text-[13px] font-medium text-maroon/80 hover:text-maroon transition-colors duration-300 after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-gold after:transition-all after:duration-300 hover:after:w-full"
                >
                  {t(n.label)}
                </a>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              <LangToggle />
              <a
                href={waLink(WA.general)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-whatsapp-button"
                className="hidden sm:inline-flex items-center gap-2 rounded-full bg-maroon px-4 py-2 text-xs font-medium text-ivory hover:bg-wine transition-colors duration-300"
              >
                <WhatsappIcon className="w-3.5 h-3.5" />
                {t(S.waConcierge)}
              </a>
              <button
                data-testid="mobile-menu-toggle"
                onClick={() => setOpen(true)}
                className="xl:hidden p-2 text-maroon"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] xl:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-burgundy/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              data-testid="mobile-menu-drawer"
              className="absolute right-0 top-0 h-full w-[82%] max-w-sm bg-ivory grain relative overflow-y-auto"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", ease: [0.16, 1, 0.3, 1], duration: 0.5 }}
            >
              <div className="flex items-center justify-between px-6 h-16 border-b border-gold/20">
                <span className="font-serif text-xl text-maroon">{t(S.brand)}</span>
                <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="p-2 text-maroon" aria-label="Close menu">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <nav className="flex flex-col px-6 py-6">
                {NAV.map((n, i) => (
                  <motion.a
                    key={n.id + n.label.en}
                    href={`#${n.id}`}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.id}`}
                    className="py-3.5 border-b border-gold/10 font-serif text-2xl text-maroon"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.05 }}
                  >
                    {t(n.label)}
                  </motion.a>
                ))}
              </nav>
              <div className="px-6 pb-10 space-y-3">
                <a
                  href={waLink(WA.general)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 rounded-full bg-maroon px-5 py-3 text-sm font-medium text-ivory"
                >
                  <WhatsappIcon className="w-4 h-4" /> {t(S.waConcierge)}
                </a>
                <a href={`tel:${CONTACT.phone}`} className="flex items-center justify-center gap-2 rounded-full border border-gold/40 px-5 py-3 text-sm font-medium text-maroon">
                  <Phone className="w-4 h-4" /> {CONTACT.phone}
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export { WhatsappIcon };
